import { useEffect } from "react";

// URL publique du site, utilisee pour ouvrir une page "vue publique" depuis
// l'application (l'app sert ses fichiers depuis capacitor://localhost : un lien
// commencant par "/" n'y designe donc aucune page consultable).
const PUBLIC_SITE_URL = "https://www.sflearning.fr";

/**
 * Liens "ouvrir dans un nouvel onglet" dans l'application mobile.
 *
 * Dans une WebView, un lien `target="_blank"` ne fait RIEN : il n'existe pas
 * d'onglet a ouvrir, et le navigateur bloque les ouvertures de fenetre. C'est
 * pour cela que la fleche d'un pack ou l'oeil d'un article restaient sans effet.
 *
 * On intercepte donc ces clics au niveau du document et on ouvre la page dans le
 * navigateur integre, exactement comme le calendrier de rendez-vous et le
 * paiement : la page s'affiche par-dessus l'app et se referme d'un bouton, sans
 * jamais quitter l'application.
 *
 * Un seul point de branchement couvre tous les liens du site, presents et a venir.
 */
export default function useNativeExternalLinks() {
  useEffect(() => {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap?.isNativePlatform?.()) return undefined;

    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const opensNewTab = anchor.target === "_blank";
      const isAbsolute = /^https?:\/\//i.test(href);

      // Liens internes normaux : on laisse React Router naviguer dans l'app.
      if (!opensNewTab && !isAbsolute) return;
      // Cas particuliers geres par le systeme (mailto:, tel:, whatsapp...).
      if (/^(mailto:|tel:|sms:)/i.test(href)) return;

      const url = isAbsolute ? href : `${PUBLIC_SITE_URL}${href.startsWith("/") ? "" : "/"}${href}`;
      const Browser = cap.Plugins?.Browser;
      if (!Browser?.open) return; // sans plugin, on laisse le comportement par defaut

      event.preventDefault();
      Promise.resolve(Browser.open({ url, presentationStyle: "popover" })).catch(() => {});
    };

    // On ecoute en phase de BULLE, donc APRES les gestionnaires des composants :
    // si l'un d'eux traite deja le clic (et appelle preventDefault), on ne fait
    // rien. En phase de capture, on ouvrait la page une seconde fois.
    document.addEventListener("click", onClick, false);
    return () => document.removeEventListener("click", onClick, false);
  }, []);
}
